import React, { useState, useEffect } from 'react';
import elasticsearch from 'elasticsearch';
import bodybuilder from 'bodybuilder';
import { ResponsiveRadar } from '@nivo/radar';

import settings from '../../helpers/settings';

const client = new elasticsearch.Client({
  host: settings.ELASTICSEARCH_HOST,
  // log: 'trace',
});

const percent = (value, decimal = 2) => {
  const r = 10 ** decimal;
  return Math.round(value * r * 100) / r;
};

const ha = value => value * 10000;

const removeBadAxes = axe => !Object.values(axe).some(value => {
  // Remove axes with no data for any zone
  if (Number.isNaN(value)) {
    return true;
  }

  // Remove axes with out of range data for any zone
  if (value < 0 || value > 100) {
    return true;
  }

  return false;
});

/**
 * Calculate total values for all fields used in radar data
 * @param {Array} zones - Array of zone objects
 * @returns {Object} Object containing totals for each field
 */
const calculateTotals = zones => {
  const fields = [
    'surf_total',
    'taux_surfdispo_tot',
    'taux_surfbati_tot',
    'nb_emplois',
    'nb_etablissement',
  ];

  return fields.reduce((totals, field) => ({
    ...totals,
    [field]: zones.reduce((sum, zone) => sum + (+zone[field] || 0), 0),
  }), {});
};

/**
 * Create a radar axis object for a given metric
 * @param {string} label - The display label for the axis
 * @param {Array} zoneValues - Array of objects with zone name and calculated percentage
 * @returns {Object} Radar axis object with label and zone percentages
 */
const createRadarAxis = (label, zoneValues) => ({
  label,
  ...zoneValues.reduce((acc, { zoneName, percentage }) => ({
    ...acc,
    [zoneName]: percentage,
  }), {}),
});

/**
 * Build radar chart data from zones
 * @param {Array} zones - Array of zone objects
 * @returns {Array} Array of radar axes with calculated percentages
 */
const buildRadarData = zones => {
  const totals = calculateTotals(zones);

  const radarMetrics = [
    {
      label: 'Surface de la zone',
      valueField: 'surf_total',
      totalField: 'surf_total',
    },
    {
      label: 'Part surface de foncier mobilisable théorique',
      valueField: 'taux_surfdispo_tot',
      totalField: 'taux_surfdispo_tot',
    },
    {
      label: 'Part surface bâtie théorique',
      valueField: 'taux_surfbati_tot',
      totalField: 'taux_surfbati_tot',
    },
    {
      label: 'Part des emplois productifs',
      valueField: 'nb_productif_eff',
      totalField: 'nb_emplois',
    },
    {
      label: 'Nombre d\'établissements',
      valueField: 'nb_etablissement',
      totalField: 'nb_etablissement',
    },
    {
      label: 'Nombre d\'emplois',
      valueField: 'nb_emplois',
      totalField: 'nb_emplois',
    },
  ];

  return radarMetrics.map(({ label, valueField, totalField }) => {
    const zoneValues = zones.map(zone => ({
      zoneName: zone.nom_ppal,
      percentage: percent(zone[valueField] / totals[totalField]),
    }));

    return createRadarAxis(label, zoneValues);
  });
};

const buildQuery = zones => ({
  body: [
    { index: 'zae' },
    bodybuilder()
      .filter('terms', 'id_eae', zones.map(({ id_eae: idEae }) => idEae))
      .rawOption('_source', { include: ['id_eae', 'nb_emplois', 'surfbati_tot', 'surfdispo_tot',
        'surf_total', 'nb_etablissement', 'nb_productif_eff', 'accessibility_tc'] })
      .build(),
  ],
});

const ZoneRadar = ({ zones, median }) => {
  const [radarData, setRadarData] = useState();
  const [colors, setColors] = useState();
  const [keys, setKeys] = useState();

  useEffect(() => {
    client.msearch(buildQuery(zones), (error, { responses }) => {
      if (!responses) {
        setRadarData(undefined);
        return;
      }

      if (!responses[0].hits.total) {
        console.error('Aucune données disponible pour cette zone.');
        setRadarData(undefined);
        return;
      }

      // Update zone data with fresh values from ES query
      const freshZones = responses[0].hits.hits.map(({ _source, _source: { id_eae: idSource } }) =>
        ({
          ...zones.find(({ id_eae: idEae }) => idEae === idSource),
          ..._source,
        }));

      // Add other missing fields (should be provided by backend)
      freshZones.forEach(freshZone => {
        if (freshZone.taux_surfdispo_tot === undefined) {
          // eslint-disable-next-line no-param-reassign
          freshZone.taux_surfdispo_tot = ha(freshZone.surfdispo_tot) / ha(freshZone.surf_total);
        }

        if (freshZone.taux_surfbati_tot === undefined) {
          // eslint-disable-next-line no-param-reassign
          freshZone.taux_surfbati_tot = freshZone.surfbati_tot / ha(freshZone.surf_total);
        }
      });

      // If there is a median, add it to data
      median && freshZones.push(median);

      if (!error) {
        const rData = buildRadarData(freshZones).filter(removeBadAxes);
        setRadarData(rData);
        setColors(freshZones.map(({ color }) => color));
        setKeys(freshZones.map(({ nom_ppal: nomPpal }) => nomPpal));
      } else {
        console.error(error);
        setRadarData(undefined);
      }
    });
  }, [zones, median]);

  if (!radarData || !keys) {
    return null;
  }

  return (
    <div style={{ height: 600 }}>
      <ResponsiveRadar
        data={radarData}
        keys={keys}
        indexBy="label"
        margin={{ top: 60, bottom: 60 }}
        maxValue={100}
        gridLevels={5}
        colors={colors}
        tooltipFormat={() => ''}
        enableDots={false}
        enableDotLabel={false}
      />
    </div>
  );
};

export default ZoneRadar;
